import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/dashboard/widgets";
import {
  Check, X, Globe, Mail, Calendar, Users, Building2, ExternalLink, Loader2, Inbox,
} from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { id: "pending_review", label: "Pending review",  hint: "Awaiting decision" },
  { id: "approved",       label: "Approved",         hint: "Affiliate accounts created" },
  { id: "rejected",       label: "Rejected",         hint: "Not a fit" },
];

const CHANNEL_LABEL = {
  newsletter: "Newsletter",
  linkedin:   "LinkedIn / X",
  podcast:    "Podcast / video",
  agency:     "Agency",
  community:  "Community",
  other:      "Other",
};

function ApplicationCard({ appn, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 bg-white border border-slate-200 rounded-xl shadow-soft hover:border-[#0055FF]/40 transition-colors duration-150 group"
      data-testid={`app-card-${appn.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 truncate">{appn.name}</div>
          <div className="text-[12px] text-slate-500 truncate font-mono">{appn.email}</div>
        </div>
        <span className="text-[10px] font-mono text-slate-400 shrink-0 tabular-nums">
          {appn.created_at ? new Date(appn.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
        </span>
      </div>
      {appn.company && (
        <div className="mt-2 text-[12.5px] text-slate-600 inline-flex items-center gap-1.5">
          <Building2 className="size-3 text-slate-400" strokeWidth={1.6} /> {appn.company}
        </div>
      )}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
          {CHANNEL_LABEL[appn.channel] || appn.channel}
        </span>
        {appn.audience_size && (
          <span className="text-[10px] font-mono uppercase tracking-widest bg-[#0055FF]/8 text-[#0055FF] px-2 py-0.5 rounded-full">
            {appn.audience_size}
          </span>
        )}
        {(appn.sectors || []).slice(0, 2).map((s) => (
          <span key={s} className="text-[10px] uppercase tracking-widest text-slate-500 px-2 py-0.5 rounded-full border border-slate-200">{s}</span>
        ))}
        {(appn.sectors || []).length > 2 && (
          <span className="text-[10px] uppercase text-slate-400 font-mono">+{appn.sectors.length - 2}</span>
        )}
      </div>
    </button>
  );
}

function ApplicationDialog({ appn, onClose, onDecided }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null); // "approve" | "reject" | null

  if (!appn) return null;
  const isPending = appn.status === "pending_review";

  const decide = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/admin/partners/applications/${appn.id}/${action}`, { note: note || null });
      toast.success(action === "approve" ? `Approved · affiliate account created${data.affiliate_id ? ` (${data.affiliate_id.slice(0, 8)})` : ""}` : "Application rejected");
      onDecided();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={!!appn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="app-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">{appn.name}</DialogTitle>
          <DialogDescription className="font-mono text-[12.5px] text-slate-500">{appn.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-[13.5px]">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Company</div>
              <div className="mt-1 text-slate-900">{appn.company || "—"}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Country</div>
              <div className="mt-1 text-slate-900 font-mono">{appn.country || "—"}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Channel</div>
              <div className="mt-1 text-slate-900">{CHANNEL_LABEL[appn.channel] || appn.channel}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Audience</div>
              <div className="mt-1 text-slate-900">{appn.audience_size || "—"}</div>
            </div>
            {appn.monthly_reach && (
              <div className="sm:col-span-2">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Monthly reach</div>
                <div className="mt-1 text-slate-900">{appn.monthly_reach}</div>
              </div>
            )}
            {appn.website && (
              <div className="sm:col-span-2">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Website</div>
                <a href={appn.website} target="_blank" rel="noreferrer" className="mt-1 text-[#0055FF] hover:underline inline-flex items-center gap-1 font-mono text-[12.5px] break-all">
                  {appn.website} <ExternalLink className="size-3" />
                </a>
              </div>
            )}
          </div>

          {(appn.sectors || []).length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Sectors</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {appn.sectors.map((s) => (
                  <span key={s} className="text-[11px] px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700">{s}</span>
                ))}
              </div>
            </div>
          )}

          {appn.motivation && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Why Connexis</div>
              <div className="mt-1.5 text-slate-700 leading-relaxed bg-slate-50/60 border border-slate-200 rounded-lg p-4">{appn.motivation}</div>
            </div>
          )}

          {!isPending && (
            <div className="text-[12px] text-slate-500 pt-2 border-t border-slate-100">
              {appn.status === "approved" ? "Approved" : "Rejected"} {appn.decided_at ? `on ${new Date(appn.decided_at).toLocaleString("en-GB")}` : ""}
              {appn.decided_by ? ` by ${appn.decided_by}` : ""}
              {appn.decision_note && <div className="mt-1.5 text-slate-700 italic">"{appn.decision_note}"</div>}
            </div>
          )}

          {isPending && (
            <div>
              <label className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">Decision note (optional)</label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note that gets stored with the decision…" className="mt-2 min-h-[80px] resize-none" data-testid="app-decision-note" />
            </div>
          )}
        </div>

        {isPending && (
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button variant="outline" onClick={() => decide("reject")} disabled={!!busy} className="rounded-full" data-testid="app-reject-btn">
              {busy === "reject" ? <Loader2 className="size-4 animate-spin" /> : <><X className="size-4" /> Reject</>}
            </Button>
            <Button onClick={() => decide("approve")} disabled={!!busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-md shadow-[#0055FF]/20" data-testid="app-approve-btn">
              {busy === "approve" ? <><Loader2 className="size-4 animate-spin mr-1" /> Approving…</> : <><Check className="size-4" /> Approve & create account</>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPartners() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/partners/applications")
       .then((r) => setRows(r.data || []))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const byStatus = (status) => rows.filter((r) => (r.status || "pending_review") === status);

  return (
    <div className="space-y-8">
      <SectionTitle
        eyebrow="Partner programme"
        title="Applications"
        subtitle="Approve incoming applications — affiliate accounts are created automatically with a welcome email."
      />

      {loading && rows.length === 0 ? (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="size-4 animate-spin" /> Loading applications…</div>
      ) : rows.length === 0 ? (
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-12 text-center" data-testid="admin-partners-empty">
            <Inbox className="size-7 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
            <div className="text-slate-700 font-medium">No applications yet</div>
            <div className="text-[13px] text-slate-500 mt-1">When prospects submit at <span className="font-mono">/partners/apply</span>, they'll appear here.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {COLUMNS.map((col) => {
            const list = byStatus(col.id);
            return (
              <div key={col.id} data-testid={`column-${col.id}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <div className="leading-tight">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">{col.hint}</div>
                    <div className="text-slate-900 font-medium mt-0.5">{col.label}</div>
                  </div>
                  <span className="text-[11px] font-mono tabular-nums text-slate-500">{list.length}</span>
                </div>
                <div className="space-y-3 min-h-[120px]">
                  {list.length === 0 ? (
                    <div className="text-[12px] text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-xl">No applications</div>
                  ) : (
                    list.map((appn) => (
                      <ApplicationCard key={appn.id} appn={appn} onClick={() => setActive(appn)} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ApplicationDialog appn={active} onClose={() => setActive(null)} onDecided={() => { setActive(null); load(); }} />
    </div>
  );
}
